// height.js
import throttle from 'lodash.throttle';

/**
 * Automatically syncs CSS variables with element heights.
 *
 * Usage:
 *   <div data-height="masthead"></div>
 *
 * Output:
 *   :root {
 *     --masthead-height: 80px;
 *     --masthead-height-number: 80;
 *   }
 *
 *   [data-height="masthead"] {
 *     --masthead-height: 80px;
 *     --masthead-height-number: 80;
 *   }
 */

export function initHeightVars() {
	const root = document.documentElement;
	if (!root) return;

	let elements = [];

	function collectElements() {
		elements = Array.from(document.querySelectorAll('[data-height]'));
	}

	// Read and write CSS variables.
	//
	// Reads (getBoundingClientRect) and writes (setProperty on :root) are split
	// into separate passes. Writing to the root element invalidates layout for
	// the whole document, so interleaving a write with the next element's read
	// forces a synchronous relayout on every iteration (layout thrashing). By
	// measuring everything first and only then writing, the browser lays out
	// once instead of once per element.
	function updateHeightVars() {
		if (!elements.length) collectElements();

		// READ pass -- measure every element before touching the DOM.
		const measurements = [];
		elements.forEach(el => {
			const name = el.getAttribute('data-height')?.trim();
			if (!name) return;
			measurements.push({
				el,
				name,
				height: Math.round(el.getBoundingClientRect().height),
			});
		});

		// WRITE pass -- no reads interleaved.
		measurements.forEach(({ el, name, height }) => {
			const pxVar = `--${name}-height`;
			const numVar = `--${name}-height-number`;

			// Set on :root
			root.style.setProperty(pxVar, `${height}px`);
			root.style.setProperty(numVar, String(height));

			// Set on the element
			el.style.setProperty(pxVar, `${height}px`);
			el.style.setProperty(numVar, String(height));
		});
	}

	// Initial run
	collectElements();
	updateHeightVars();

	// Throttled updates
	const throttledUpdate = throttle(updateHeightVars, 100);

	window.addEventListener('resize', throttledUpdate);
	window.addEventListener('scroll', throttledUpdate, { passive: true });

	// Detect new data-height elements dynamically. Throttled: during initial
	// page load the body subtree mutates constantly (images, lazy content), and
	// an unthrottled callback re-measured every [data-height] element on each
	// mutation -- a large, repeated source of forced reflow. Reuse the same
	// 100ms throttle window as resize/scroll.
	const throttledCollectUpdate = throttle(() => {
		collectElements();
		updateHeightVars();
	}, 100);

	const observer = new MutationObserver(throttledCollectUpdate);

	observer.observe(document.body, {
		subtree: true,
		childList: true,
	});
}
